'use client';

import { use, useCallback, useMemo } from 'react';
import { InstantSearchNext } from 'react-instantsearch-nextjs';
import camelCase from 'lodash-es/camelCase';
import { BuyerListingFavorite } from '@/legacy/favorites/FavoritesViewModel';
import { useSearchClient } from '@/lib/algolia/clientProvider';
import { PercentOffMsrpClassification } from '@/types/PercentOffMsrpClassification';
import { FilterAttribute } from '@/types/filterAttribute';
import { WishlistsWithMetadata } from '../../wishlist/types';
import AllInventory from '../AllInventory';
import { HandpickedForYouSection } from '../HandpickedForYou';
import {
  BuyerPreferenceListingCollectionsFactories,
  FlatBuyerPreferences,
  ListingHomePageRecommendation,
} from '../types';
import ListingCollection from './ListingCollection';
import RecommendationCollection from './RecommendationCollection';

const STREETWEAR = 'Streetwear';

interface OrderedListingCollectionGroupProps {
  categoriesPromise: Promise<{ [key: string]: string[] }>;
  favoritesPromise: Promise<BuyerListingFavorite[]>;
  isForYouSectionFeatureEnabled: boolean;
  listingHomePageRecommendationsPromise: Promise<ListingHomePageRecommendation[]>;
  listingsIndexName: string;
  msrpClassificationsPromise: Promise<PercentOffMsrpClassification[]>;
  preferencesPromise: Promise<FlatBuyerPreferences>;
  wishlistsPromise: Promise<WishlistsWithMetadata>;
  isStockXSectionEnabled: boolean;
}
type IndexedElementFactory = (index: number) => JSX.Element;

export const OrderedListingCollectionGroup = ({
  categoriesPromise,
  favoritesPromise,
  isForYouSectionFeatureEnabled,
  listingHomePageRecommendationsPromise,
  listingsIndexName,
  msrpClassificationsPromise,
  preferencesPromise,
  wishlistsPromise,
  isStockXSectionEnabled,
}: OrderedListingCollectionGroupProps) => {
  const preferences = use(preferencesPromise);

  const { searchClient } = useSearchClient();

  // Map buyer preference to associated RecommendationCollection
  const preferenceBasedListingCollections: BuyerPreferenceListingCollectionsFactories = useMemo(
    () => ({
      percentOffMsrp: (index) => (
        <RecommendationCollection
          key="percent-off-msrp"
          index={index}
          listingsIndexName={listingsIndexName}
          sectionTitle="Your preferred discount"
          numPerPage={4}
          navigateTo="/recommendations/percentOffMsrp"
          favoritesPromise={favoritesPromise}
          filterAttribute="percentOffMsrp"
          preferencesPromise={preferencesPromise}
          msrpClassificationsPromise={msrpClassificationsPromise}
          isStockXSectionEnabled={isStockXSectionEnabled}
        />
      ),
      platforms: (index) => (
        <RecommendationCollection
          key="platforms"
          index={index}
          listingsIndexName={listingsIndexName}
          sectionTitle="For your marketplaces"
          numPerPage={4}
          navigateTo="/recommendations/platforms"
          favoritesPromise={favoritesPromise}
          filterAttribute="platforms"
          preferencesPromise={preferencesPromise}
          msrpClassificationsPromise={msrpClassificationsPromise}
          isStockXSectionEnabled={isStockXSectionEnabled}
        />
      ),
      specialties: (index) => (
        <RecommendationCollection
          key="specialties"
          index={index}
          listingsIndexName={listingsIndexName}
          sectionTitle="Specialties for you"
          numPerPage={4}
          navigateTo="/recommendations/specialties"
          favoritesPromise={favoritesPromise}
          filterAttribute="specialties"
          preferencesPromise={preferencesPromise}
          msrpClassificationsPromise={msrpClassificationsPromise}
          isStockXSectionEnabled={isStockXSectionEnabled}
        />
      ),
      brands: (index) => (
        <RecommendationCollection
          key="brands"
          index={index}
          listingsIndexName={listingsIndexName}
          sectionTitle="Brands for you"
          numPerPage={4}
          navigateTo="/recommendations/brands"
          favoritesPromise={favoritesPromise}
          filterAttribute="brands"
          preferencesPromise={preferencesPromise}
          msrpClassificationsPromise={msrpClassificationsPromise}
          isStockXSectionEnabled={isStockXSectionEnabled}
        />
      ),
      compositeCategories: (index) => (
        <RecommendationCollection
          key="compositeCategories"
          index={index}
          listingsIndexName={listingsIndexName}
          sectionTitle="Categories for you"
          numPerPage={4}
          navigateTo="/recommendations/compositeCategories"
          favoritesPromise={favoritesPromise}
          filterAttribute="compositeCategories"
          preferencesPromise={preferencesPromise}
          msrpClassificationsPromise={msrpClassificationsPromise}
          isStockXSectionEnabled={isStockXSectionEnabled}
        />
      ),
    }),
    [
      favoritesPromise,
      listingsIndexName,
      msrpClassificationsPromise,
      preferencesPromise,
      isStockXSectionEnabled,
    ],
  );
  // Array of default ListingCollections with null values where RecommendationCollections could go
  const defaultCollectionsOrder: (IndexedElementFactory | null)[] = useMemo(
    () => [
      ...(isForYouSectionFeatureEnabled
        ? [
            () => (
              <HandpickedForYouSection
                categoriesPromise={categoriesPromise}
                favoritesPromise={favoritesPromise}
                key="handpicked-for-you"
                listingHomePageRecommendationsPromise={listingHomePageRecommendationsPromise}
                listingsIndexName={listingsIndexName}
                preferencesPromise={preferencesPromise}
                wishlistsPromise={wishlistsPromise}
                isStockXSectionEnabled={isStockXSectionEnabled}
              />
            ),
          ]
        : []),
      null,
      null,
      (index) => (
        <ListingCollection
          key="new"
          index={index}
          listingsIndexName={listingsIndexName}
          sectionTitle="New on Ghost"
          numPerPage={4}
          navigateTo="/collection/new"
          favoritesPromise={favoritesPromise}
          preferencesPromise={preferencesPromise}
          collection="new"
          preferenceDependencies={['platforms', 'specialties']}
          isStockXSectionEnabled={isStockXSectionEnabled}
        />
      ),
      null,
      null,
      (index) => (
        <ListingCollection
          key="featured"
          index={index}
          listingsIndexName={listingsIndexName}
          sectionTitle="Featured Inventory"
          numPerPage={4}
          navigateTo="/collection/featured"
          favoritesPromise={favoritesPromise}
          preferencesPromise={preferencesPromise}
          collection="featured"
          preferenceDependencies={['brands']}
          isStockXSectionEnabled={isStockXSectionEnabled}
        />
      ),
      null,
      (index) => (
        <ListingCollection
          key="trending"
          index={index}
          listingsIndexName={listingsIndexName}
          sectionTitle="Trending"
          numPerPage={5}
          navigateTo="/collection/trending"
          favoritesPromise={favoritesPromise}
          preferencesPromise={preferencesPromise}
          collection="trending"
          isStockXSectionEnabled={isStockXSectionEnabled}
        />
      ),
      () => (
        <AllInventory
          key="all"
          favoritesPromise={favoritesPromise}
          listingsIndexName={listingsIndexName}
          isStockXSectionEnabled={isStockXSectionEnabled}
        />
      ),
    ],
    [
      categoriesPromise,
      favoritesPromise,
      isForYouSectionFeatureEnabled,
      listingHomePageRecommendationsPromise,
      listingsIndexName,
      preferencesPromise,
      wishlistsPromise,
      isStockXSectionEnabled,
    ],
  );
  // Callback to replace null values in defaultCollectionsOrder with newly reordered RecommendationCollections
  const combineRecommendationCollectionsWithDefault = useCallback(
    (recommendationCollectionFactories: IndexedElementFactory[]): JSX.Element[] => {
      let recommendationCollectionsIndex = 0;
      return defaultCollectionsOrder.map((factory, index) => {
        // null case: replace with RecommendationCollection
        if (!factory) {
          const recommendationCollection = recommendationCollectionFactories[
            recommendationCollectionsIndex
          ](index + 1);
          recommendationCollectionsIndex++;
          return recommendationCollection;
        }
        return factory(index + 1);
      });
    },
    [defaultCollectionsOrder],
  );
  // The final, ordered list of Collections to be rendered
  const orderedListingCollectionGroup: (JSX.Element | null)[] = useMemo(() => {
    const {
      platforms,
      specialties: specialtiesFactory,
      brands,
      compositeCategories,
      percentOffMsrp,
    } = preferenceBasedListingCollections;
    const recommendationCollectionsDefaultOrder = [
      platforms,
      specialtiesFactory,
      brands,
      compositeCategories,
      percentOffMsrp,
    ];
    const { percentOffMsrpClassificationId, specialties, adminPrimaryOverride } = preferences;
    // if an admin has selected a collection to prioritize then surface that one regardless of preferences
    if (adminPrimaryOverride) {
      const filterAttribute = camelCase(adminPrimaryOverride);
      const adminPrimaryIndex = recommendationCollectionsDefaultOrder.findIndex(
        (factory) => factory(0).props.filterAttribute === filterAttribute,
      );
      return combineRecommendationCollectionsWithDefault([
        preferenceBasedListingCollections[filterAttribute as FilterAttribute],
        ...recommendationCollectionsDefaultOrder.toSpliced(adminPrimaryIndex, 1),
      ]);
    }
    // if no admin override and "Streetwear" is selected as one of their specialties we want to surface that collection
    if (specialties.includes(STREETWEAR)) {
      const specialtiesCollectionIndex = recommendationCollectionsDefaultOrder.findIndex(
        (factory) => factory(0).props.filterAttribute === 'specialties',
      );
      return combineRecommendationCollectionsWithDefault([
        specialtiesFactory,
        ...recommendationCollectionsDefaultOrder.toSpliced(specialtiesCollectionIndex, 1),
      ]);
    }
    // if Streetwear was not a selected specialty but a discount preference was specified then surface that collection
    if (percentOffMsrpClassificationId) {
      const msrpCollectionIndex = recommendationCollectionsDefaultOrder.findIndex(
        (factory) => factory(0).props.filterAttribute === 'percentOffMsrp',
      );
      return combineRecommendationCollectionsWithDefault([
        percentOffMsrp,
        ...recommendationCollectionsDefaultOrder.toSpliced(msrpCollectionIndex, 1),
      ]);
    }
    // otherwise, return the current order of recommendations and defaults
    return combineRecommendationCollectionsWithDefault(recommendationCollectionsDefaultOrder);
  }, [combineRecommendationCollectionsWithDefault, preferenceBasedListingCollections, preferences]);

  return (
    <InstantSearchNext
      searchClient={searchClient}
      indexName={listingsIndexName}
      insights={{ insightsClient: null }}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      {orderedListingCollectionGroup}
    </InstantSearchNext>
  );
};
